import { useEffect, useState } from "react";
import { Container, Label, Form, FormGroup } from "reactstrap";
import { useHistory, useParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";

import { HeadingWithBackArrow } from "../../../components/HeadingWithBackArrow";
import { LoaderFallbackUI } from "../../../components/LoaderFallbackUI";
import { TextInput } from "../../../components/Input";
import { CustomButton } from "../../../components/CustomButton";
import FormikErrorText from "../../../components/FormikErrorText/FormikErrorText";
import typography from "../typography";
import { APP_ROUTES } from "../../../helpers/routeHelpers";
import { getResetActivityContent, updateResetActivityContent } from "../../../api/api.service";
import { initializeMediaUtil, uploadOnS3, finalizeMediaUtil } from "../../../utils/mediaUtils";
import { ALLOWED_IMAGE_EXTENSIONS, ALLOWED_MUSIC_EXTENSIONS } from "../data";

// One built-in reset's content: its name, card line, goal, description, teacher
// instructions, tip, picture and background music (BuiltInActivityContent).
//
// Unlike a locked session, nothing here is ever empty: every activity is seeded
// with its launch copy, picture and music, so the form opens filled in and
// every text field is required. The picture and the music can be replaced by
// an upload, and put back to the default bundled with the web app.
//
// Reached from the Manage Activity listing (LockedSessions.js).
const RESET_LABELS = {
    BIG_HUG_SQUEEZE: { name: "Big Hug Squeeze", stateLabel: typography.COMFORT },
    HAND_COMPRESSION: { name: "Hand Compression", stateLabel: typography.COMFORT },
    COLOR_HUNT: { name: "Color Hunt", stateLabel: typography.CLARITY },
    THUMB_PEEK: { name: "Thumb Peek", stateLabel: typography.CLARITY },
    CROSS_BODY_SWITCH: { name: "Cross-Body Switch", stateLabel: typography.ENERGY },
    STANDING_CROSS_MARCH: { name: "Standing Cross March", stateLabel: typography.ENERGY },
    SEATED_CROSS_CRAWL: { name: "Seated Cross Crawl", stateLabel: typography.FOCUS },
    CROSS_CRAWL_TAP: { name: "Cross-Crawl Tap", stateLabel: typography.FOCUS },
    ALTERNATING_TAPS: { name: "Alternating Taps", stateLabel: typography.CALM },
};

const TEXT_FIELDS = ["title", "summary", "goal", "description", "teacherInstructions", "tipForTeachers"];

const validationSchema = Yup.object({
    title: Yup.string().trim().required(typography.REQUIRED).max(120),
    summary: Yup.string().trim().required(typography.REQUIRED).max(200),
    goal: Yup.string().trim().required(typography.REQUIRED).max(300),
    description: Yup.string().trim().required(typography.REQUIRED).max(2000),
    teacherInstructions: Yup.string().trim().required(typography.REQUIRED).max(2000),
    tipForTeachers: Yup.string().trim().required(typography.REQUIRED).max(600),
});

const extensionOf = (file) => file.name.split(".").pop()?.toLowerCase();

export default function EditResetActivity() {
    const { activity } = useParams();
    const history = useHistory();
    const labels = RESET_LABELS[activity];

    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [saved, setSaved] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    const [thumbnailFile, setThumbnailFile] = useState(null);
    const [thumbnailError, setThumbnailError] = useState("");
    const [thumbnailProgress, setThumbnailProgress] = useState(0);
    const [restoreThumbnail, setRestoreThumbnail] = useState(false);

    const [musicFile, setMusicFile] = useState(null);
    const [musicError, setMusicError] = useState("");
    const [musicProgress, setMusicProgress] = useState(0);
    const [restoreMusic, setRestoreMusic] = useState(false);
    const [musicVolume, setMusicVolume] = useState(30);
    const [musicEnabled, setMusicEnabled] = useState(true);

    const formik = useFormik({
        initialValues: Object.fromEntries(TEXT_FIELDS.map((field) => [field, ""])),
        validationSchema,
        onSubmit: handleSaveClick,
    });

    const applySaved = (content) => {
        setSaved(content);
        formik.setValues(Object.fromEntries(TEXT_FIELDS.map((field) => [field, content[field] || ""])));
        setMusicVolume(content.backgroundMusicVolume ?? 30);
        setMusicEnabled(content.backgroundMusicEnabled !== false);
        setThumbnailFile(null);
        setMusicFile(null);
        setRestoreThumbnail(false);
        setRestoreMusic(false);
    };

    useEffect(() => {
        if (!labels) {
            setLoadError("Unknown activity.");
            setLoading(false);
            return;
        }
        let cancelled = false;
        setLoading(true);
        setLoadError(null);
        // The API helper resolves with the response body itself (no `.data`
        // envelope) — see EditLockedSession.js.
        getResetActivityContent(activity)
            .then((content) => {
                if (!cancelled && content) applySaved(content);
            })
            .catch(() => {
                if (!cancelled) setLoadError("Failed to load this activity.");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line
    }, [activity]);

    const backButtonHandler = () => {
        history.push(APP_ROUTES.LOCKED_SESSION_CONTENT);
    };

    const pickFile = (allowed, setFile, setError, setRestore) => (e) => {
        const selected = e.target.files?.[0];
        if (!selected) return;
        if (!allowed.includes(extensionOf(selected))) {
            setError(`Unsupported file type. Allowed: ${allowed.join(", ")}`);
            setFile(null);
            return;
        }
        setError("");
        setFile(selected);
        setRestore(false);
    };

    // The same upload flow AddContent.js uses: IMAGE for the picture, VIDEO for
    // audio (the shared Media model has no AUDIO type).
    const upload = async (file, type, onProgress) => {
        const credentials = await initializeMediaUtil(file, false, type);
        await uploadOnS3(file, credentials, onProgress);
        return finalizeMediaUtil(credentials?.mediaId, false);
    };

    async function handleSaveClick(values) {
        try {
            setIsSaving(true);
            const thumbnailMedia = thumbnailFile ? await upload(thumbnailFile, "IMAGE", setThumbnailProgress) : null;
            const musicMedia = musicFile ? await upload(musicFile, "VIDEO", setMusicProgress) : null;

            const bodyData = {
                ...Object.fromEntries(TEXT_FIELDS.map((field) => [field, values[field].trim()])),
                backgroundMusicVolume: Number(musicVolume),
                backgroundMusicEnabled: musicEnabled,
                // A new upload replaces; "Use default" sends null, which puts the
                // bundled default back; neither leaves the field untouched.
                ...(thumbnailMedia ? { thumbnailMediaId: thumbnailMedia.id } : restoreThumbnail ? { thumbnailMediaId: null } : {}),
                ...(musicMedia ? { backgroundMusicMediaId: musicMedia.id } : restoreMusic ? { backgroundMusicMediaId: null } : {}),
            };

            const updated = await updateResetActivityContent({ activity, bodyData });
            if (updated) applySaved(updated);
            toast.success("Activity updated");
        } catch (error) {
            console.error(`Error updating ${activity}:`, error);
            toast.error("Failed to save. Please try again.");
        } finally {
            setIsSaving(false);
            setThumbnailProgress(0);
            setMusicProgress(0);
        }
    }

    const heading = (
        <div className="edit-notification-heading">
            <HeadingWithBackArrow
                backArrowClickHandler={backButtonHandler}
                headingTitle={labels ? `${typography.EDIT_LOCKED_SESSION} — ${labels.name} (${labels.stateLabel})` : typography.EDIT_LOCKED_SESSION}
                showToggle={false}
            />
        </div>
    );

    if (loading) return <LoaderFallbackUI />;

    if (loadError || !saved) {
        return (
            <div className="page-content">
                <Container fluid className="customer-management">
                    {heading}
                    <div className="standard-typo-14-400">{loadError || "Failed to load this activity."}</div>
                </Container>
            </div>
        );
    }

    // What the teacher will see and hear once saved.
    const shownThumbnail = restoreThumbnail ? null : saved.thumbnailUrl;
    const thumbnailStatus = thumbnailFile
        ? thumbnailFile.name
        : restoreThumbnail
          ? "The default picture will be used after saving"
          : saved.thumbnailIsDefault
            ? `${typography.DEFAULT_ASSET} picture`
            : `${typography.UPLOADED_ASSET} picture — choose a file to replace`;
    const shownMusic = restoreMusic ? null : saved.backgroundMusicUrl;
    const musicStatus = musicFile
        ? musicFile.name
        : restoreMusic
          ? "The default track will be used after saving"
          : saved.backgroundMusicIsDefault
            ? `${typography.DEFAULT_ASSET} track`
            : `${typography.UPLOADED_ASSET} track — choose a file to replace`;

    const textField = (name, label, help, { multiline = false, rows = 3 } = {}) => (
        <FormGroup>
            <Label className="edit-notification-form-label" for={`reset-${name}`}>
                {label}
            </Label>
            {help ? <div className="standard-typo-14-400 mb-2">{help}</div> : null}
            {multiline ? (
                <textarea
                    id={`reset-${name}`}
                    name={name}
                    className="edit-notification-form-textarea"
                    value={formik.values[name]}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    rows={rows}
                />
            ) : (
                <TextInput
                    id={`reset-${name}`}
                    name={name}
                    value={formik.values[name]}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className="edit-notification-form-input hide-default-icon"
                />
            )}
            <FormikErrorText formikInstance={formik} fieldName={name} />
        </FormGroup>
    );

    return (
        <div className="page-content">
            <Container fluid className="customer-management">
                {heading}

                <Form onSubmit={formik.handleSubmit} className="edit-notification-form">
                    {textField("title", typography.TITLE, typography.RESET_TITLE_HELP)}
                    {textField("summary", typography.SUMMARY, typography.SUMMARY_HELP)}
                    {textField("goal", typography.GOAL, typography.GOAL_HELP)}
                    {textField("description", typography.DESCRIPTION, null, { multiline: true })}
                    {textField("teacherInstructions", typography.TEACHER_INSTRUCTIONS, typography.TEACHER_INSTRUCTIONS_STEPS_HELP, {
                        multiline: true,
                        rows: 6,
                    })}
                    {textField("tipForTeachers", typography.TIP_FOR_TEACHERS, typography.TIP_FOR_TEACHERS_HELP, {
                        multiline: true,
                    })}

                    <FormGroup>
                        <Label className="edit-notification-form-label" for="reset-thumbnail-file">
                            {typography.THUMBNAIL}
                        </Label>
                        <div className="content-file-upload-con">
                            {shownThumbnail && !thumbnailFile ? (
                                <img src={shownThumbnail} alt="" className="locked-session-thumb-preview mb-2" />
                            ) : null}
                            <input
                                id="reset-thumbnail-file"
                                type="file"
                                accept={ALLOWED_IMAGE_EXTENSIONS.map((ext) => `.${ext}`).join(",")}
                                onChange={pickFile(ALLOWED_IMAGE_EXTENSIONS, setThumbnailFile, setThumbnailError, setRestoreThumbnail)}
                            />
                            <div className="mt-2 standard-typo-14-400">{thumbnailStatus}</div>
                            {!saved.thumbnailIsDefault && !restoreThumbnail && !thumbnailFile ? (
                                <button type="button" className="reset-asset-restore" onClick={() => setRestoreThumbnail(true)}>
                                    {typography.RESTORE_DEFAULT}
                                </button>
                            ) : null}
                            {isSaving && thumbnailFile ? (
                                <div className="content-file-progress">
                                    <div className="content-file-progress-bar" style={{ width: `${thumbnailProgress}%` }} />
                                </div>
                            ) : null}
                            {thumbnailError ? <div className="invalid-feedback d-block">{thumbnailError}</div> : null}
                        </div>
                    </FormGroup>

                    <FormGroup>
                        <Label className="edit-notification-form-label" for="reset-music-file">
                            {typography.BACKGROUND_MUSIC}
                        </Label>
                        <div className="standard-typo-14-400 mb-2">{typography.BACKGROUND_MUSIC_HELP}</div>
                        <div className="content-file-upload-con">
                            <label className="reset-music-enabled">
                                <input
                                    type="checkbox"
                                    checked={musicEnabled}
                                    onChange={(e) => setMusicEnabled(e.target.checked)}
                                />
                                <span>{typography.MUSIC_ENABLED}</span>
                            </label>

                            {shownMusic && !musicFile ? (
                                // eslint-disable-next-line jsx-a11y/media-has-caption
                                <audio className="reset-music-player" controls loop preload="none" src={shownMusic} />
                            ) : null}
                            <input
                                id="reset-music-file"
                                type="file"
                                accept={ALLOWED_MUSIC_EXTENSIONS.map((ext) => `.${ext}`).join(",")}
                                onChange={pickFile(ALLOWED_MUSIC_EXTENSIONS, setMusicFile, setMusicError, setRestoreMusic)}
                            />
                            <div className="mt-2 standard-typo-14-400">{musicStatus}</div>
                            {!saved.backgroundMusicIsDefault && !restoreMusic && !musicFile ? (
                                <button type="button" className="reset-asset-restore" onClick={() => setRestoreMusic(true)}>
                                    {typography.RESTORE_DEFAULT}
                                </button>
                            ) : null}
                            {isSaving && musicFile ? (
                                <div className="content-file-progress">
                                    <div className="content-file-progress-bar" style={{ width: `${musicProgress}%` }} />
                                </div>
                            ) : null}
                            {musicError ? <div className="invalid-feedback d-block">{musicError}</div> : null}

                            <label className="reset-music-volume" htmlFor="reset-music-volume">
                                <span>
                                    {typography.MUSIC_VOLUME}: {musicVolume}%
                                </span>
                                <input
                                    id="reset-music-volume"
                                    type="range"
                                    min={0}
                                    max={100}
                                    step={5}
                                    value={musicVolume}
                                    disabled={!musicEnabled}
                                    onChange={(e) => setMusicVolume(Number(e.target.value))}
                                />
                            </label>
                        </div>
                    </FormGroup>

                    <CustomButton
                        title={isSaving ? "Saving…" : "Save"}
                        className="edit-notification-update-button"
                        onClick={formik.handleSubmit}
                        type="submit"
                        loading={isSaving}
                    />
                </Form>
            </Container>
        </div>
    );
}
